import React, { useState, useEffect, useRef } from 'react';
import { api } from '../../App';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { 
  Search, Filter, UserCheck, UserX, Clock, Ban, Trash2,
  CheckCircle, XCircle, AlertCircle, Gift, Smartphone, Download,
  Upload, RotateCcw, Database, FileUp, X
} from 'lucide-react';
import { toast } from 'sonner';
import UserActionsModal from './UserActionsModal';

export default function UsersTab({ onUpdate }) {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showActionsModal, setShowActionsModal] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [showRestoreModal, setShowRestoreModal] = useState(false);
  const [backups, setBackups] = useState([]);
  const [loadingBackups, setLoadingBackups] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [restoreMode, setRestoreMode] = useState('merge');
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchUsers();
  }, [statusFilter, page]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '50'
      });
      
      if (statusFilter !== 'all') {
        params.append('status', statusFilter);
      }
      
      if (search) {
        params.append('search', search);
      }

      const response = await api.get(`/admin/users?${params.toString()}`);
      // Sort users by created_at descending (newest first) to ensure proper ordering
      const sortedUsers = [...response.data.users].sort((a, b) => {
        const dateA = new Date(a.created_at || 0);
        const dateB = new Date(b.created_at || 0);
        return dateB - dateA; // Descending order (newest first)
      });
      setUsers(sortedUsers);
      setTotal(response.data.total);
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPage(1);
    fetchUsers();
  };

  const handleExportUsers = async () => {
    setExporting(true);
    try {
      const response = await api.get('/admin/users/export');
      const data = response.data;
      
      // Create downloadable JSON file
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Generate filename with date
      const date = new Date().toISOString().split('T')[0];
      link.download = `lexprocure_users_backup_${date}.json`;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success(`Backup complete! ${data.total_users} users exported to your downloads AND saved to database (ID: ${data.backup_id})`);
    } catch (error) {
      toast.error('Failed to export users');
      console.error('Export error:', error);
    } finally {
      setExporting(false);
    }
  };

  const fetchBackups = async () => {
    setLoadingBackups(true);
    try {
      const response = await api.get('/admin/backups');
      setBackups(response.data.backups || []);
    } catch (error) {
      toast.error('Failed to load backups');
    } finally {
      setLoadingBackups(false);
    }
  };

  const handleOpenRestoreModal = () => {
    setShowRestoreModal(true);
    fetchBackups();
  };

  const handleRestoreFromBackup = async (backupId) => {
    if (!window.confirm(`Are you sure you want to restore from this backup?\n\nMode: ${restoreMode === 'merge' ? 'Merge (add new users, update existing)' : 'Replace (delete all users first)'}\n\nA safety backup will be created automatically.`)) {
      return;
    }
    
    setRestoring(true);
    try {
      const response = await api.post('/admin/users/restore', {
        backup_id: backupId,
        mode: restoreMode
      });
      
      const { restored, updated, skipped, errors } = response.data;
      toast.success(`Restore complete! Added: ${restored}, Updated: ${updated}, Skipped: ${skipped}`);
      
      if (errors && errors.length > 0) {
        toast.warning(`${errors.length} errors occurred during restore`);
      }
      
      setShowRestoreModal(false);
      fetchUsers();
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to restore users');
    } finally {
      setRestoring(false);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    if (!file.name.endsWith('.json')) {
      toast.error('Please upload a JSON backup file');
      return;
    }
    
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      
      if (!data.users || !Array.isArray(data.users)) {
        toast.error('Invalid backup file format. Expected "users" array.');
        return;
      }
      
      if (!window.confirm(`Found ${data.users.length} users in backup file.\n\nMode: ${restoreMode === 'merge' ? 'Merge (add new users, update existing)' : 'Replace (delete all users first)'}\n\nProceed with restore?`)) {
        return;
      }
      
      setRestoring(true);
      const response = await api.post('/admin/users/restore', {
        users: data.users,
        mode: restoreMode
      });
      
      const { restored, updated, skipped, errors } = response.data;
      toast.success(`Restore complete! Added: ${restored}, Updated: ${updated}, Skipped: ${skipped}`);
      
      if (errors && errors.length > 0) {
        toast.warning(`${errors.length} errors occurred during restore`);
      }
      
      setShowRestoreModal(false);
      fetchUsers();
      if (onUpdate) onUpdate();
    } catch (error) {
      if (error instanceof SyntaxError) {
        toast.error('Invalid JSON file');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to restore users');
      }
    } finally {
      setRestoring(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDeleteBackup = async (backupId) => {
    if (!window.confirm('Are you sure you want to delete this backup? This cannot be undone.')) {
      return;
    }
    
    try {
      await api.delete(`/admin/backups/${backupId}`);
      toast.success('Backup deleted');
      fetchBackups();
    } catch (error) {
      toast.error('Failed to delete backup');
    }
  };

  const getStatusBadge = (user) => {
    // Use computed_status if available, otherwise fall back to subscription_status
    const status = user.computed_status || user.subscription_status;
    
    if (user.is_banned) {
      return <Badge variant="destructive" className="flex items-center gap-1 bg-red-500 text-white">
        <Ban className="w-3 h-3" /> Banned
      </Badge>;
    }
    
    if (status === 'active') {
      return <Badge variant="default" className="bg-emerald-500 text-white flex items-center gap-1">
        <CheckCircle className="w-3 h-3" /> Active
      </Badge>;
    }
    
    if (status === 'trial') {
      return <Badge variant="default" className="bg-blue-500 text-white flex items-center gap-1">
        <Clock className="w-3 h-3" /> Trial
      </Badge>;
    }
    
    if (status === 'expired') {
      return <Badge variant="secondary" className="flex items-center gap-1 bg-gray-200 text-gray-900">
        <XCircle className="w-3 h-3" /> Expired
      </Badge>;
    }
    
    return <Badge variant="secondary" className="flex items-center gap-1 bg-gray-200 text-gray-900">
      <XCircle className="w-3 h-3" /> Expired
    </Badge>;
  };

  const getExtensionBadge = (user) => {
    // Extension granted - show success badge
    if (user.extension_granted === true) {
      return (
        <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-300 flex items-center gap-1">
          <CheckCircle className="w-3 h-3" /> Extended (+{user.extension_days || 5} days)
        </Badge>
      );
    }
    
    // Extension pending - show warning badge
    if (user.extension_requested && user.extension_granted === null) {
      return (
        <Badge className="bg-yellow-100 text-yellow-800 border border-yellow-300 flex items-center gap-1">
          <Clock className="w-3 h-3" /> Extension Pending
        </Badge>
      );
    }
    
    // Extension rejected - show error badge
    if (user.extension_granted === false) {
      return (
        <Badge className="bg-red-100 text-red-800 border border-red-300 flex items-center gap-1">
          <XCircle className="w-3 h-3" /> Extension Rejected
        </Badge>
      );
    }
    
    return null;
  };

  const getReferralInfo = (user) => {
    if (user.referred_by) {
      return (
        <div className="flex items-center gap-1" title={`Referred by: ${user.referred_by}`}>
          <Gift className="w-3 h-3 text-purple-600" />
          <span className="text-xs text-purple-600 font-medium">Referred</span>
        </div>
      );
    }
    return <span className="text-xs text-gray-400">—</span>;
  };

  const getRoleBadge = (role) => {
    if (role === 'admin') {
      return <Badge variant="default" className="bg-purple-500 text-white">Admin</Badge>;
    }
    if (role === 'premium') {
      return <Badge variant="default" className="bg-yellow-500 text-white">Subscriber</Badge>;
    }
    return <Badge variant="outline" className="bg-white text-gray-900 border-gray-300">Trial</Badge>;
  };

  const calculateDaysRemaining = (user) => {
    if (user.subscription_status === 'trial' && user.trial_end_date) {
      let endDate = new Date(user.trial_end_date);
      
      // If extension was granted, add extension days
      if (user.extension_granted === true && user.extension_days) {
        endDate = new Date(endDate.getTime() + (user.extension_days * 24 * 60 * 60 * 1000));
      }
      
      const now = new Date();
      const days = Math.ceil((endDate - now) / (1000 * 60 * 60 * 24));
      return days > 0 ? days : 0;
    }
    if (user.subscription_status === 'active' && user.subscription_end_date) {
      const endDate = new Date(user.subscription_end_date);
      const now = new Date();
      const days = Math.ceil((endDate - now) / (1000 * 60 * 60 * 24));
      return days > 0 ? days : 0;
    }
    return 0;
  };

  const handleUserAction = (user) => {
    setSelectedUser(user);
    setShowActionsModal(true);
  };

  const handleModalClose = (updated) => {
    setShowActionsModal(false);
    setSelectedUser(null);
    if (updated) {
      fetchUsers();
      onUpdate();
    }
  };

  return (
    <div className="space-y-4">
      <Card className="overflow-hidden">
        <CardHeader className="flex flex-row items-center justify-between px-4 sm:px-6">
          <CardTitle className="text-lg sm:text-xl">User Management</CardTitle>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleOpenRestoreModal}
              className="border-blue-300 text-blue-700 hover:bg-blue-50 shrink-0"
            >
              <RotateCcw className="w-4 h-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Restore</span>
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleExportUsers}
              disabled={exporting}
              className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 shrink-0"
            >
              <Download className="w-4 h-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">{exporting ? 'Exporting...' : 'Export Backup'}</span>
              <span className="sm:hidden">{exporting ? '...' : 'Export'}</span>
            </Button>
          </div>
        </CardHeader>
        <CardContent className="px-4 sm:px-6">
          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="flex-1 flex gap-2">
              <Input
                placeholder="Search by email or name..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                className="flex-1"
              />
              <Button onClick={handleSearch} size="icon">
                <Search className="w-4 h-4" />
              </Button>
            </div>
            
            <div className="flex gap-2 flex-wrap">
              <Button
                variant={statusFilter === 'all' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setStatusFilter('all')}
              >
                All
              </Button>
              <Button
                variant={statusFilter === 'trial' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setStatusFilter('trial')}
              >
                Trial
              </Button>
              <Button
                variant={statusFilter === 'active' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setStatusFilter('active')}
              >
                Active
              </Button>
              <Button
                variant={statusFilter === 'expired' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setStatusFilter('expired')}
              >
                Expired
              </Button>
              <Button
                variant={statusFilter === 'banned' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setStatusFilter('banned')}
                className="border-red-300 text-red-600 hover:bg-red-50"
              >
                <Ban className="w-3 h-3 mr-1" />
                Banned
              </Button>
            </div>
          </div>

          {/* Users Table */}
          {loading ? (
            <div className="text-center py-8 text-gray-500">Loading users...</div>
          ) : users.length === 0 ? (
            <div className="text-center py-8 text-gray-500">No users found</div>
          ) : (
            <>
              {/* Mobile scroll hint */}
              <p className="text-xs text-gray-500 md:hidden mb-2 flex items-center gap-1">
                <AlertCircle className="w-3 h-3 flex-shrink-0" />
                Scroll horizontally to see all columns →
              </p>
              <div className="overflow-x-auto -mx-4 sm:-mx-6">
                <div className="inline-block min-w-full align-middle px-4 sm:px-6">
                  <table className="min-w-full bg-white">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">User</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Role</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Extension</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Sessions</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Referral</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Days Left</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Registered</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-700 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {users.map((user) => (
                    <tr key={user.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div>
                          <p className="font-medium text-gray-900">{user.full_name}</p>
                          <p className="text-sm text-gray-500">{user.email}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {getRoleBadge(user.role)}
                      </td>
                      <td className="px-4 py-3">
                        {getStatusBadge(user)}
                      </td>
                      <td className="px-4 py-3">
                        {getExtensionBadge(user) || (
                          <span className="text-xs text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <Badge 
                          variant={user.active_devices >= 2 ? "default" : "secondary"}
                          className={user.active_devices >= 2 ? "bg-blue-500 text-white" : "bg-gray-200 text-gray-900"}
                        >
                          {user.active_devices || 0}/2
                        </Badge>
                      </td>
                      <td className="px-4 py-3">
                        {getReferralInfo(user)}
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-gray-900">
                          {calculateDaysRemaining(user)} days
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-gray-500">
                          {new Date(user.created_at).toLocaleDateString()}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleUserAction(user)}
                          className="bg-white text-gray-900 border-gray-300 hover:bg-gray-50"
                        >
                          Manage
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
                </div>
              </div>
            </>
          )}

          {/* Pagination */}
          {total > 50 && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t">
              <p className="text-sm text-gray-500">
                Showing {(page - 1) * 50 + 1} to {Math.min(page * 50, total)} of {total} users
              </p>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  Previous
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setPage(p => p + 1)}
                  disabled={page * 50 >= total}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* User Actions Modal */}
      {showActionsModal && selectedUser && (
        <UserActionsModal
          user={selectedUser}
          currentUser={currentUser}
          onClose={handleModalClose}
        />
      )}

      {/* Restore Users Modal */}
      {showRestoreModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-xl font-semibold text-gray-900">Restore Users from Backup</h2>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowRestoreModal(false)}
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
            
            <div className="p-4 overflow-y-auto max-h-[calc(90vh-130px)]">
              {/* Restore Mode Selection */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">Restore Mode</label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="restoreMode"
                      value="merge"
                      checked={restoreMode === 'merge'}
                      onChange={(e) => setRestoreMode(e.target.value)}
                      className="text-emerald-600"
                    />
                    <span className="text-sm">
                      <strong>Merge</strong> - Add new users, update existing
                    </span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="restoreMode"
                      value="replace"
                      checked={restoreMode === 'replace'}
                      onChange={(e) => setRestoreMode(e.target.value)}
                      className="text-red-600"
                    />
                    <span className="text-sm">
                      <strong className="text-red-600">Replace</strong> - Delete all users first
                    </span>
                  </label>
                </div>
                {restoreMode === 'replace' && (
                  <p className="text-xs text-red-600 mt-2">
                    ⚠️ Replace mode will delete all existing users (except you) before restoring. A safety backup will be created automatically.
                  </p>
                )}
              </div>

              {/* Upload from File */}
              <div className="mb-6 p-4 border-2 border-dashed border-gray-300 rounded-lg">
                <div className="flex items-center gap-3 mb-3">
                  <FileUp className="w-6 h-6 text-blue-500" />
                  <div>
                    <h3 className="font-medium text-gray-900">Restore from File</h3>
                    <p className="text-sm text-gray-500">Upload a previously downloaded JSON backup file</p>
                  </div>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".json,application/json"
                  onChange={handleFileUpload}
                  className="sr-only"
                  id="backup-file-input"
                  data-testid="backup-file-input"
                />
                <Button
                  variant="outline"
                  type="button"
                  disabled={restoring}
                  className="w-full"
                  data-testid="select-backup-file-btn"
                  onClick={() => {
                    const input = document.getElementById('backup-file-input');
                    if (input) input.click();
                  }}
                >
                  <Upload className="w-4 h-4 mr-2" />
                  {restoring ? 'Restoring...' : 'Select Backup File'}
                </Button>
              </div>

              {/* Restore from Database */}
              <div className="border rounded-lg">
                <div className="flex items-center gap-3 p-4 border-b bg-gray-50">
                  <Database className="w-6 h-6 text-emerald-500" />
                  <div>
                    <h3 className="font-medium text-gray-900">Restore from Database</h3>
                    <p className="text-sm text-gray-500">Select from previously saved backups</p>
                  </div>
                </div>
                
                {loadingBackups ? (
                  <div className="p-4 text-center text-gray-500">Loading backups...</div>
                ) : backups.length === 0 ? (
                  <div className="p-4 text-center text-gray-500">No backups found in database</div>
                ) : (
                  <div className="divide-y max-h-64 overflow-y-auto">
                    {backups.map((backup) => (
                      <div key={backup.id} className="p-4 flex items-center justify-between hover:bg-gray-50">
                        <div>
                          <p className="font-medium text-gray-900">{backup.id}</p>
                          <p className="text-sm text-gray-500">
                            {new Date(backup.export_date).toLocaleString()} • {backup.total_users} users
                          </p>
                          <p className="text-xs text-gray-400">by {backup.exported_by}</p>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            onClick={() => handleRestoreFromBackup(backup.id)}
                            disabled={restoring}
                            className="bg-emerald-600 hover:bg-emerald-700"
                          >
                            {restoring ? '...' : 'Restore'}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDeleteBackup(backup.id)}
                            className="text-red-600 border-red-300 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            
            <div className="p-4 border-t bg-gray-50">
              <Button
                variant="outline"
                onClick={() => setShowRestoreModal(false)}
                className="w-full"
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
